import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import pluralize from "pluralize";
import { IsFeatureReady } from "src/constants/features";
import { UserKeys } from "src/constants/userDetails";
import * as PERMISSIONS from "src/constants/permissions";
import { customNames } from "src/utils/helpers";
import {
    MONTH_ROW_MIN_HEIGHT,
    MONTH_SPAN_TOP_OFFSET,
    MONTH_SPAN_ITEM_HEIGHT,
    MONTH_SPAN_ROW_GAP,
    MONTH_DAY_ITEM_HEIGHT,
    MONTH_DAY_ITEM_GAP,
    MONTH_DAY_FOOTER_SPACE,
    MONTH_DAY_EXTRA_BOTTOM_SPACE,
} from "../components";
import {
    AUDIENCE_COLORS,
    AUTOMATION_ITEM_TYPE_COLORS,
    AUTOMATION_ITEM_TYPES,
    EVENT_SOURCE,
    formatTimezoneLabel,
    getCalendarItemSourceKey,
    getTimezonePlaceName,
    SHARED_AUDIENCE,
    SOURCE_COLORS,
    TASK_PRIORITIES,
    TASK_REPEAT_OPTIONS,
    TASK_TYPE_COLORS,
    TASK_TYPES,
} from "../calendarData";
import { timezoneCountryCodes } from "../timezoneCountries";
import type {
    AnalyticsPeriod,
    AnalyticsVisibility,
    BusyWindow,
    CalendarAttachment,
    CalendarItem,
    CalendarItemForm,
    CalendarUser,
    DateRange,
    EventSource,
    MeetingSummary,
    ReminderOffset,
    RightTabOption,
    SharedAudience,
    SmartTimeSuggestion,
    SuggestionFeedItem,
    TaskPrefill,
    TaskPriority,
    TaskRepeatOption,
    TaskType,
    TimezoneCalculator,
    TimezoneTone,
    ViewOption,
    VisibilityGroupKey,
    VisibilityRecipients,
    WeekSpanLane
} from "../types";

export const priorityTone: Record<TaskPriority, string> = {
    [TASK_PRIORITIES.HIGH]: "#ef6f8f",
    [TASK_PRIORITIES.MEDIUM]: "#f4c95d",
    [TASK_PRIORITIES.LOW]: "#62d7a8",
};

export const priorityLabel: Record<TaskPriority, string> = {
    [TASK_PRIORITIES.HIGH]: "High",
    [TASK_PRIORITIES.MEDIUM]: "Med",
    [TASK_PRIORITIES.LOW]: "Low",
};

export const VISIBILITY_RECIPIENT_GROUPS: Record<VisibilityGroupKey, {
    audience: SharedAudience;
    isClient: boolean;
    color: string;
}> = {
    employees: {
        audience: SHARED_AUDIENCE.EMPLOYEES,
        isClient: false,
        color: AUDIENCE_COLORS[SHARED_AUDIENCE.EMPLOYEES],
    },
    clients: {
        audience: SHARED_AUDIENCE.CLIENTS,
        isClient: true,
        color: AUDIENCE_COLORS[SHARED_AUDIENCE.CLIENTS],
    },
};

export const smartFeatures = [
    { icon: "🧘", title: "Wellness Buffer Zones", text: "Auto-blocks recovery time after back-to-back meetings", ai: true, tint: "#34d399" },
    { icon: "🌏", title: "Time Zone Empathy", text: "Flags bookings outside a colleague's reasonable hours", ai: false, tint: "#60a5fa" },
    { icon: "⚡", title: "Focus Mode Shield", text: "Locks deep work blocks and auto-declines requests", ai: false, tint: "#facc15" },
    { icon: "🤖", title: "AI Schedule Suggest", text: "Instantly finds the optimal slot for all attendees", ai: true, tint: "#8b5cf6" },
    { icon: "📊", title: "Meeting Load Score", text: "Alerts when your week exceeds healthy meeting thresholds", ai: true, tint: "#fb7185" },
    { icon: "🎯", title: "Goal-Aware Scheduling", text: "Protects time around active goal deadlines", ai: true, tint: "#f472b6" },
];

export const THEME_TOGGLE_LIGHT_FRAME = 70;
export const THEME_TOGGLE_DARK_FRAME = 0;
export const THEME_TOGGLE_DARK_TO_LIGHT_SEGMENT: [number, number] = [THEME_TOGGLE_DARK_FRAME, THEME_TOGGLE_LIGHT_FRAME];
export const THEME_TOGGLE_LIGHT_TO_DARK_SEGMENT: [number, number] = [THEME_TOGGLE_LIGHT_FRAME, 140];

export const VIEW_OPTIONS: ViewOption[] = ["Day", "Week", "Month", "Year"];
export const RIGHT_TABS: RightTabOption[] = ["To-Do", "Meetings", "Today"];
export const VISIBLE_RIGHT_TABS = RIGHT_TABS
    .filter(tab => tab !== "Meetings" || IsFeatureReady.calendarMeetingRequests());
export const ANALYTICS_PERIOD_OPTIONS: AnalyticsPeriod[] = ["Weekly", "Monthly", "Yearly"];
export const ANALYTICS_VISIBILITY_OPTIONS: AnalyticsVisibility[] = ["All", "Personal", "Employees", "Clients", "Both"];
export const REMINDER_OPTIONS = [
    { value: "none", label: "No reminder" },
    { value: "0", label: "At start time" },
    { value: "5", label: "5 min before" },
    { value: "15", label: "15 min before" },
    { value: "30", label: "30 min before" },
    { value: "60", label: "1 hour before" },
    { value: "120", label: "2 hours before" },
    { value: "1440", label: "1 day before" },
];
export const TIME_PICKER_OPTIONS = Array.from({ length: 96 }, (_, index) => {
    const hour = Math.floor(index / 4);
    const minute = (index % 4) * 15;
    const value = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
    return {
        value,
        label: dayjs(`2000-01-01T${value}`).format("h:mm A"),
    };
});
export type ModalErrors = Partial<Record<string, string>>;

export const DEFAULT_START_TIME = "09:00";
export const DEFAULT_END_TIME = "10:00";

export const formatTimeOptionLabel = (value?: string): string => {
    if (!value) return "";
    const time = dayjs(`2000-01-01T${value}`);
    return time.isValid() ? time.format("h:mm A") : value;
};

export const getCustomGroupName = (groupKey: VisibilityGroupKey, singular = false): string => {
    const base = groupKey === "employees" ? customNames.employees : customNames.clients;
    return singular ? pluralize.singular(base) : pluralize(base);
};

export const getCustomGroupNameLower = (groupKey: VisibilityGroupKey, singular = false): string => getCustomGroupName(groupKey, singular).toLowerCase();

export const getDisplayAudienceLabel = (audience: SharedAudience): string => {
    if (audience === SHARED_AUDIENCE.PERSONAL) return "Personal";
    if (audience === SHARED_AUDIENCE.EMPLOYEES) return getCustomGroupName("employees");
    if (audience === SHARED_AUDIENCE.CLIENTS) return getCustomGroupName("clients");
    if (audience === SHARED_AUDIENCE.BOTH) return `${getCustomGroupName("employees")} & ${getCustomGroupName("clients")}`;
    return "Unknown";
};

export const getVisibilityRecipientGroup = (groupKey: VisibilityGroupKey) => ({
    ...VISIBILITY_RECIPIENT_GROUPS[groupKey],
    label: getCustomGroupName(groupKey),
    singularLabel: getCustomGroupName(groupKey, true),
});

export const getAudienceOptions = (): Array<{ value: SharedAudience; label: string }> => [
    { value: SHARED_AUDIENCE.PERSONAL, label: "Personal" },
    { value: SHARED_AUDIENCE.EMPLOYEES, label: getCustomGroupName("employees") },
    { value: SHARED_AUDIENCE.CLIENTS, label: getCustomGroupName("clients") },
    { value: SHARED_AUDIENCE.BOTH, label: getDisplayAudienceLabel(SHARED_AUDIENCE.BOTH) },
];

export const getAudienceOptionsForUser = (user: CalendarUser | null | undefined): Array<{ value: SharedAudience; label: string }> => {
    const audienceOptions = getAudienceOptions();
    const isClient = Boolean(user?.[UserKeys.IS_CLIENT]);
    const canViewEmployees = !isClient || hasCalendarEmployeeAdminPermission(user);
    const canViewClients = isClient || hasCalendarClientAdminPermission(user);

    return audienceOptions.filter(({ value }) => (
        value === SHARED_AUDIENCE.PERSONAL
        || (value === SHARED_AUDIENCE.EMPLOYEES && canViewEmployees)
        || (value === SHARED_AUDIENCE.CLIENTS && canViewClients)
        || (value === SHARED_AUDIENCE.BOTH && canViewClients)
    ));
};

export const getDisplayRecipientLabel = (recipients: string | undefined, fallbackAudience: SharedAudience): string => {
    if (!recipients) return getDisplayAudienceLabel(fallbackAudience);
    if (recipients === "Employees") return getCustomGroupName("employees");
    if (recipients === "Clients") return getCustomGroupName("clients");
    if (recipients === "Employees & Clients") return getDisplayAudienceLabel(SHARED_AUDIENCE.BOTH);
    return recipients;
};

export const REMINDER_LABELS: Record<string, string> = REMINDER_OPTIONS.reduce((acc, option) => {
    acc[option.value] = option.label;
    return acc;
}, {} as Record<string, string>);
export const MANUAL_ITEM_TASK_TYPES = new Set<TaskType>([
    TASK_TYPES.TASK,
    TASK_TYPES.MEETING,
    TASK_TYPES.REMINDER,
]);
export const AUTOMATION_TASK_TYPE_KEYS = new Set<TaskType>([
    AUTOMATION_ITEM_TYPES.NOTICEBOARD,
    AUTOMATION_ITEM_TYPES.GOAL_TRACKING,
    AUTOMATION_ITEM_TYPES.HABIT,
    AUTOMATION_ITEM_TYPES.MOOD,
    AUTOMATION_ITEM_TYPES.CHECKLIST,
    AUTOMATION_ITEM_TYPES.EMAIL_CAMPAIGN,
    AUTOMATION_ITEM_TYPES.SURVEY,
    AUTOMATION_ITEM_TYPES.POLL,
]);
export const CONNECTED_EVENT_SOURCES = new Set<EventSource>([
    EVENT_SOURCE.GOOGLE,
    EVENT_SOURCE.OUTLOOK,
    EVENT_SOURCE.APPLE,
]);
export const isManualItemTaskType = (taskType: TaskType | ""): taskType is TaskType => Boolean(taskType) && !AUTOMATION_TASK_TYPE_KEYS.has(taskType as TaskType);

export type TaskFormSource = (Partial<Omit<CalendarItem, "attachments" | "meeting">> & Partial<Omit<CalendarItemForm, "attachments" | "meeting">> & {
    attachments?: CalendarAttachment[];
    meeting?: CalendarItem["meeting"] | CalendarItemForm["meeting"];
}) | null | undefined;
export type DateBucket = { label: string; title: string; start: Dayjs; end: Dayjs };
export type ViewStep = { amount: number; unit: "day" | "week" | "month" | "year" };
export type SuggestionCandidateDraft = Omit<SuggestionFeedItem, "prefill"> & { prefill: TaskPrefill | null };

export const getAudienceRecipientGroups = (audience: SharedAudience): VisibilityGroupKey[] => {
    if (audience === SHARED_AUDIENCE.EMPLOYEES) return ["employees"];
    if (audience === SHARED_AUDIENCE.CLIENTS) return ["clients"];
    if (audience === SHARED_AUDIENCE.BOTH) return ["employees", "clients"];
    return [];
};

export const emptyVisibilityRecipients = (): VisibilityRecipients => ({
    employees: { all: false, selectedEmployeeUuids: [] },
    clients: { all: false, selectedEmployeeUuids: [] },
});

export const normaliseVisibilityRecipients = (recipients?: VisibilityRecipients | null): VisibilityRecipients => {
    if (!recipients) return emptyVisibilityRecipients();

    return {
        employees: {
            all: Boolean(recipients.employees.all),
            selectedEmployeeUuids: recipients.employees.selectedEmployeeUuids,
        },
        clients: {
            all: Boolean(recipients.clients.all),
            selectedEmployeeUuids: recipients.clients.selectedEmployeeUuids,
        },
    };
};

export const getVisibilityRecipientsForAudience = (audience: SharedAudience, previousRecipients?: VisibilityRecipients | null): VisibilityRecipients => {
    const recipients = normaliseVisibilityRecipients(previousRecipients);
    const activeGroups = getAudienceRecipientGroups(audience);
    const next = emptyVisibilityRecipients();

    activeGroups.forEach((groupKey) => {
        const previous = recipients[groupKey];
        next[groupKey] = previous.all || previous.selectedEmployeeUuids.length > 0
            ? previous
            : { all: true, selectedEmployeeUuids: [] };
    });

    return next;
};

export const getVisibilityRecipientSummary = (groupKey: VisibilityGroupKey, recipients: VisibilityRecipients): string => {
    const group = getVisibilityRecipientGroup(groupKey);
    const state = recipients[groupKey];
    if (state.all) return `All ${group.label}`;
    if (state.selectedEmployeeUuids.length === 1) return `1 ${group.singularLabel} selected`;
    if (state.selectedEmployeeUuids.length > 1) return `${state.selectedEmployeeUuids.length} ${group.label} selected`;
    return `No ${group.label.toLowerCase()} selected`;
};

export const getVisibilitySubsetAssignments = (item: CalendarItem | null | undefined) => {
    if (!item || item.audience === SHARED_AUDIENCE.PERSONAL) return [];

    const recipients = normaliseVisibilityRecipients(item.visibilityRecipients);
    return getAudienceRecipientGroups(item.audience)
        .map((groupKey) => {
            const group = getVisibilityRecipientGroup(groupKey);
            const groupState = recipients[groupKey];
            return {
                groupKey,
                group,
                selectedEmployeeUuids: groupState.selectedEmployeeUuids,
                all: groupState.all,
            };
        })
        .filter(({ all, selectedEmployeeUuids }) => !all && selectedEmployeeUuids.length > 0);
};

export const calendarItemFormDefaults = (item: TaskFormSource, selectedDate: Dayjs): CalendarItemForm => {
    const scheduledStart = item?.startAt ? dayjs(item.startAt) : null;
    const scheduledEnd = item?.endAt ? dayjs(item.endAt) : null;
    const startDate = scheduledStart?.format("YYYY-MM-DD") || item?.startDate || item?.dueDate || selectedDate.format("YYYY-MM-DD");
    const endDate = scheduledEnd?.format("YYYY-MM-DD") || item?.endDate || item?.startDate || item?.dueDate || selectedDate.format("YYYY-MM-DD");
    const taskType = item?.taskType || TASK_TYPES.TASK;

    return {
        title: item?.title || "",
        startDate,
        startTime: item?.allDay ? "" : scheduledStart?.format("HH:mm") || item?.startTime || item?.dueTime || DEFAULT_START_TIME,
        endDate,
        endTime: item?.allDay ? "" : scheduledEnd?.format("HH:mm") || item?.endTime || item?.startTime || item?.dueTime || DEFAULT_END_TIME,
        allDay: Boolean(item?.allDay),
        description: item?.description || "",
        completed: item?.completed || false,
        itemTypeUuid: item?.itemTypeUuid || taskType,
        taskType,
        priority: item?.priority || TASK_PRIORITIES.MEDIUM,
        color: item?.color || TASK_TYPE_COLORS[taskType] || AUTOMATION_ITEM_TYPE_COLORS[taskType] || SOURCE_COLORS[EVENT_SOURCE.MANUAL],
        reminderOffset: item?.reminderOffset || "none",
        repeat: item?.repeat || (item?.recurring ? TASK_REPEAT_OPTIONS.EVERY_WEEK : TASK_REPEAT_OPTIONS.NONE),
        audience: item?.audience || SHARED_AUDIENCE.PERSONAL,
        visibilityRecipients: getVisibilityRecipientsForAudience(item?.audience || SHARED_AUDIENCE.PERSONAL, item?.visibilityRecipients),
        meetingUrl: item?.meetingUrl || "",
        invites: item?.invites || "",
        location: item?.location || "",
        url: item?.url || "",
        attachments: normaliseAttachments(item?.attachments),
        meeting: {
            enabled: Boolean(item?.meeting),
            timezone: item?.meeting?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
            internalParticipantUuids: item?.meeting?.internalParticipantUuids || [],
            guestEmails: item?.meeting?.guestEmails || [],
            passwordEnabled: Boolean(item?.meeting?.passwordEnabled),
            password: "",
        },
    };
};

export const getUserString = (user: CalendarUser | null | undefined, key: string): string => {
    const value = user?.[key];
    return typeof value === "string" ? value : "";
};

export const getUserPermissions = (user: CalendarUser | null | undefined): Array<string | number> => {
    const permissions = user?.[UserKeys.PERMISSIONS];
    return Array.isArray(permissions) ? permissions : [];
};

export const getUserAudience = (user: CalendarUser | null | undefined): SharedAudience => user?.[UserKeys.IS_CLIENT] ? SHARED_AUDIENCE.CLIENTS : SHARED_AUDIENCE.EMPLOYEES;
export const isOwnItem = (item: CalendarItem, user: CalendarUser | null | undefined): boolean => Boolean(item.ownerEmail && item.ownerEmail === getUserString(user, UserKeys.EMAIL));

export const getCalendarUserEmployeeUuid = (user: CalendarUser | null | undefined): string => (
    getUserString(user, UserKeys.EMPLOYEE_UUID) || getUserString(user, UserKeys.UUID)
);
export const hasScheduledCalendarFields = (item: CalendarItem | null | undefined): boolean => Boolean(item && item.startAt && item.endAt);
export const hasActionCalendarFields = (item: CalendarItem | null | undefined): boolean => Boolean(
    item
    && !hasScheduledCalendarFields(item)
    && (
        "itemTypeUuid" in item
        || "taskType" in item
        || "startDate" in item
        || "dueDate" in item
        || "completed" in item
        || "progress" in item
    )
);
export const getCalendarItemMode = (item: CalendarItem): "scheduled" | "task" => hasScheduledCalendarFields(item) ? "scheduled" : "task";

const hasCalendarAdminPermission = (user: CalendarUser | null | undefined, permission: number): boolean => {
    const permissions = getUserPermissions(user);
    return permissions.includes(PERMISSIONS.ACCOUNT_OWNER_PERMISSION) || permissions.includes(permission);
};

export const hasCalendarEmployeeAdminPermission = (user: CalendarUser | null | undefined): boolean => (
    hasCalendarAdminPermission(user, PERMISSIONS.CALENDAR_EMPLOYEE_ADMIN)
);

export const hasCalendarClientAdminPermission = (user: CalendarUser | null | undefined): boolean => (
    hasCalendarAdminPermission(user, PERMISSIONS.CALENDAR_CLIENT_ADMIN)
);

export const canManageCalendarAudience = (user: CalendarUser | null | undefined, audience: SharedAudience): boolean => {
    if (audience === SHARED_AUDIENCE.PERSONAL) {
        return true;
    }

    return getAudienceRecipientGroups(audience).every((groupKey) => (
        groupKey === "employees"
            ? hasCalendarEmployeeAdminPermission(user)
            : hasCalendarClientAdminPermission(user)
    ));
};

export const getAudienceOptionsUserCanManage = (user: CalendarUser | null | undefined): Array<{ value: SharedAudience; label: string }> => (
    getAudienceOptions().filter(({ value }) => canManageCalendarAudience(user, value))
);

export const canManageSharedCalendarItems = (user: CalendarUser | null | undefined): boolean => {
    return hasCalendarEmployeeAdminPermission(user) || hasCalendarClientAdminPermission(user);
};

export const getAutomationFeature = (item: CalendarItem | null | undefined): string => {
    if (!item) return "";
    if (item.automationDetail?.feature) return item.automationDetail.feature;
    if (hasActionCalendarFields(item) && item.taskType && AUTOMATION_TASK_TYPE_KEYS.has(item.taskType)) return item.taskType;
    if (hasScheduledCalendarFields(item)) {
        const sourceKey = getCalendarItemSourceKey(item.source);
        if (sourceKey === EVENT_SOURCE.NOTICEBOARD) return "noticeboard";
        if (sourceKey === EVENT_SOURCE.EMAIL_CAMPAIGN) return "emailCampaign";
        if (sourceKey === EVENT_SOURCE.GOAL) return "goal";
        if (sourceKey === EVENT_SOURCE.HABIT) return "habit";
        if (sourceKey === EVENT_SOURCE.MOOD) return "mood";
        if (sourceKey === EVENT_SOURCE.TEAM_GOAL) return "teamGoal";
        if (sourceKey === EVENT_SOURCE.CHECKLIST) return "checklist";
        if (sourceKey === EVENT_SOURCE.SURVEY) return "survey";
        if (sourceKey === EVENT_SOURCE.POLL) return "poll";
    }
    return "";
};

export const isAutomationItem = (item: CalendarItem): boolean => Boolean(getAutomationFeature(item));
export const isManualActionCalendarItem = (item: CalendarItem | null | undefined): boolean => (
    hasActionCalendarFields(item)
    && Boolean(item.itemTypeUuid)
    && !isAutomationItem(item)
);

export const getAutomationTaskTypeFilterKey = (item: CalendarItem | null | undefined): TaskType | "" => {
    const automationFeature = getAutomationFeature(item);
    if (automationFeature === EVENT_SOURCE.NOTICEBOARD) return AUTOMATION_ITEM_TYPES.NOTICEBOARD;
    if (automationFeature === EVENT_SOURCE.EMAIL_CAMPAIGN) return AUTOMATION_ITEM_TYPES.EMAIL_CAMPAIGN;
    if (automationFeature === EVENT_SOURCE.GOAL || automationFeature === EVENT_SOURCE.TEAM_GOAL) return AUTOMATION_ITEM_TYPES.GOAL_TRACKING;
    if (automationFeature === EVENT_SOURCE.HABIT) return AUTOMATION_ITEM_TYPES.HABIT;
    if (automationFeature === EVENT_SOURCE.MOOD) return AUTOMATION_ITEM_TYPES.MOOD;
    if (automationFeature === EVENT_SOURCE.CHECKLIST) return AUTOMATION_ITEM_TYPES.CHECKLIST;
    if (automationFeature === EVENT_SOURCE.SURVEY) return AUTOMATION_ITEM_TYPES.SURVEY;
    if (automationFeature === EVENT_SOURCE.POLL) return AUTOMATION_ITEM_TYPES.POLL;
    return "";
};

export const getScheduledItemTaskTypeFilterKey = (item: CalendarItem | null | undefined): TaskType | "" => {
    if (!hasScheduledCalendarFields(item)) return "";
    const automationFilterKey = getAutomationTaskTypeFilterKey(item);
    if (automationFilterKey) return automationFilterKey;
    return "";
};

export const getCalendarItemDisplayColor = (item: CalendarItem): string => {
    if (hasScheduledCalendarFields(item)) {
        const automationFilterKey = getScheduledItemTaskTypeFilterKey(item);
        if (automationFilterKey) return AUTOMATION_ITEM_TYPE_COLORS[automationFilterKey] || item.color;
        const sourceKey = getCalendarItemSourceKey(item.source);
        if (sourceKey) return SOURCE_COLORS[sourceKey] || item.color;
    }
    if (hasActionCalendarFields(item)) {
        const automationFilterKey = getAutomationTaskTypeFilterKey(item);
        if (automationFilterKey) return AUTOMATION_ITEM_TYPE_COLORS[automationFilterKey] || item.color;
        if (item.taskType && AUTOMATION_TASK_TYPE_KEYS.has(item.taskType)) {
            return AUTOMATION_ITEM_TYPE_COLORS[item.taskType] || item.color;
        }
    }
    return item.color;
};

const canViewSharedCalendarItem = (item: CalendarItem, user: CalendarUser | null | undefined): boolean => {
    const userAudience: VisibilityGroupKey = user?.[UserKeys.IS_CLIENT] ? "clients" : "employees";
    const recipients = normaliseVisibilityRecipients(item.visibilityRecipients);
    const userUuid = getCalendarUserEmployeeUuid(user);
    const userRecipients = recipients[userAudience];

    if (getAudienceRecipientGroups(item.audience).includes(userAudience)
        && (userRecipients.all || userRecipients.selectedEmployeeUuids.includes(userUuid))) {
        return true;
    }

    return canManageCalendarAudience(user, item.audience);
};

const getMeetingSummary = (item: CalendarItem): MeetingSummary | null => {
    if (item.meeting && "meetingUuid" in item.meeting) {
        return item.meeting;
    }
    return null;
};

const isInternalMeetingParticipant = (
    item: CalendarItem,
    user: CalendarUser | null | undefined
): boolean => {
    const meeting = getMeetingSummary(item);
    const userUuid = getCalendarUserEmployeeUuid(user);
    return Boolean(
        meeting
        && userUuid
        && (
            meeting.hostEmployeeUuid === userUuid
            || meeting.internalParticipantUuids.includes(userUuid)
        )
    );
};

export const isScheduledItemVisible = (item: CalendarItem, user: CalendarUser | null | undefined, connectionsBySource: Partial<Record<EventSource, boolean>>): boolean => {
    if (!hasScheduledCalendarFields(item)) return false;
    if (isInternalMeetingParticipant(item, user)) {
        return true;
    }
    const sourceKey = getCalendarItemSourceKey(item.source);
    if (sourceKey && CONNECTED_EVENT_SOURCES.has(sourceKey)) {
        return Boolean(connectionsBySource[sourceKey]);
    }
    if (item.audience === SHARED_AUDIENCE.PERSONAL) {
        return item.ownerEmail ? isOwnItem(item, user) : true;
    }
    return canViewSharedCalendarItem(item, user);
};

export const isTaskVisible = (item: CalendarItem, user: CalendarUser | null | undefined): boolean => {
    if (!hasActionCalendarFields(item)) return false;
    if (item.audience === SHARED_AUDIENCE.PERSONAL) {
        return item.ownerEmail ? isOwnItem(item, user) : true;
    }
    return canViewSharedCalendarItem(item, user);
};

export const getItemPermissions = (item: CalendarItem, user: CalendarUser | null | undefined): { canEdit: boolean; canDelete: boolean } => {
    const meeting = getMeetingSummary(item);
    if (meeting) {
        const canManageMeeting = meeting.hostEmployeeUuid === getCalendarUserEmployeeUuid(user)
            || isOwnItem(item, user);
        return { canEdit: canManageMeeting, canDelete: canManageMeeting };
    }
    const canManageItemAudience = canManageCalendarAudience(user, item.audience);
    if (hasActionCalendarFields(item)) {
        if (isAutomationItem(item)) {
            return { canEdit: false, canDelete: false };
        }
        if (item.audience !== SHARED_AUDIENCE.PERSONAL) {
            return { canEdit: canManageItemAudience, canDelete: canManageItemAudience };
        }
        return { canEdit: isOwnItem(item, user), canDelete: isOwnItem(item, user) };
    }
    if (getCalendarItemSourceKey(item.source)) {
        return { canEdit: false, canDelete: false };
    }
    if (item.audience === SHARED_AUDIENCE.PERSONAL) {
        return { canEdit: isOwnItem(item, user), canDelete: isOwnItem(item, user) };
    }
    return { canEdit: canManageItemAudience, canDelete: canManageItemAudience };
};

export const scheduledItemOverlapsDate = (item: CalendarItem, date: Dayjs, timezone?: string): boolean => {
    const start = (timezone ? getDateInTimezone(item.startAt, timezone) : dayjs(item.startAt)).startOf("day");
    const end = (timezone ? getDateInTimezone(item.endAt, timezone) : dayjs(item.endAt)).startOf("day");
    return !date.startOf("day").isBefore(start) && !date.startOf("day").isAfter(end);
};

export const getTaskStartDay = (task: Pick<CalendarItem, "startDate" | "dueDate">): Dayjs => dayjs(task.startDate || task.dueDate).startOf("day");
export const getTaskEndDay = (task: Pick<CalendarItem, "endDate" | "startDate" | "dueDate">): Dayjs => dayjs(task.endDate || task.startDate || task.dueDate).startOf("day");

export const taskOverlapsDate = (task: CalendarItem, date: Dayjs): boolean => {
    const start = getTaskStartDay(task);
    const end = getTaskEndDay(task);
    return !date.startOf("day").isBefore(start) && !date.startOf("day").isAfter(end);
};

type AutomationEndpointKind = "start" | "end";

export const isSurveyOrPollAutomationItem = (item: CalendarItem | null | undefined): boolean => {
    const filterKey = getAutomationTaskTypeFilterKey(item);
    return filterKey === AUTOMATION_ITEM_TYPES.SURVEY || filterKey === AUTOMATION_ITEM_TYPES.POLL;
};

const getCalendarItemStartDateTime = (item: CalendarItem, timezone?: string): Dayjs => (
    hasScheduledCalendarFields(item)
        ? (timezone ? getDateTimeInTimezone(item.startAt, timezone) : dayjs(item.startAt))
        : getTaskDateTimeRange(item).start
);

const getCalendarItemEndDateTime = (item: CalendarItem, timezone?: string): Dayjs => (
    hasScheduledCalendarFields(item)
        ? (timezone ? getDateTimeInTimezone(item.endAt, timezone) : dayjs(item.endAt))
        : getTaskDateTimeRange(item).end
);

export const getAutomationEndpointKindsForCell = (
    item: CalendarItem,
    cellStart: Dayjs,
    unit: "day" | "hour",
    timezone?: string
): AutomationEndpointKind[] => {
    if (!isSurveyOrPollAutomationItem(item)) return [];

    const start = getCalendarItemStartDateTime(item, timezone);
    const end = getCalendarItemEndDateTime(item, timezone);
    return [
        start.isSame(cellStart, unit) ? "start" : null,
        end.isSame(cellStart, unit) ? "end" : null,
    ].filter((kind): kind is AutomationEndpointKind => Boolean(kind));
};

export const automationEndpointOverlapsDate = (item: CalendarItem, date: Dayjs, timezone?: string): boolean => (
    getAutomationEndpointKindsForCell(item, date.startOf("day"), "day", timezone).length > 0
);

export const automationEndpointTouchesHour = (item: CalendarItem, date: Dayjs, hour: number, timezone?: string): boolean => (
    getAutomationEndpointKindsForCell(item, date.startOf("day").hour(hour), "hour", timezone).length > 0
);

export const getAutomationEndpointTitle = (item: CalendarItem, cellStart: Dayjs, unit: "day" | "hour", timezone?: string): string => {
    const endpointKinds = getAutomationEndpointKindsForCell(item, cellStart, unit, timezone);
    if (endpointKinds.length !== 1) return item.title;
    return `${endpointKinds[0] === "start" ? "Start" : "End"}: ${item.title}`;
};

type RepeatingCalendarItem = CalendarItem & {
    repeat?: TaskRepeatOption;
    recurring?: boolean;
};

const getRepeatUnit = (repeat: TaskRepeatOption | undefined): "day" | "week" | "month" | "year" | null => {
    if (repeat === TASK_REPEAT_OPTIONS.EVERY_DAY) return "day";
    if (repeat === TASK_REPEAT_OPTIONS.EVERY_WEEK) return "week";
    if (repeat === TASK_REPEAT_OPTIONS.EVERY_MONTH) return "month";
    if (repeat === TASK_REPEAT_OPTIONS.EVERY_YEAR) return "year";
    return null;
};

const getItemRepeatUnit = (item: RepeatingCalendarItem): "day" | "week" | "month" | "year" | null => {
    const repeat = item.repeat || (item.recurring ? TASK_REPEAT_OPTIONS.EVERY_WEEK : TASK_REPEAT_OPTIONS.NONE);
    return getRepeatUnit(repeat);
};

const shiftCalendarTaskOccurrence = (task: CalendarItem, occurrenceStart: Dayjs, baseStart: Dayjs): CalendarItem => {
    const shiftDate = (value: string | undefined): string => {
        if (!value) return "";
        const date = dayjs(value);
        return date.isValid()
            ? occurrenceStart.add(date.startOf("day").diff(baseStart, "day"), "day").format("YYYY-MM-DD")
            : value;
    };
    const occurrenceKey = occurrenceStart.format("YYYY-MM-DD");

    return {
        ...task,
        id: occurrenceStart.isSame(baseStart, "day") ? task.id : `${task.id}-${occurrenceKey}`,
        startDate: shiftDate(task.startDate),
        endDate: shiftDate(task.endDate),
        dueDate: shiftDate(task.dueDate),
    };
};

const shiftCalendarItemOccurrence = (item: CalendarItem, occurrenceStart: Dayjs, baseStart: Dayjs): CalendarItem => {
    const offset = occurrenceStart.startOf("day").diff(baseStart.startOf("day"));
    const occurrenceKey = occurrenceStart.format("YYYY-MM-DD");

    return {
        ...item,
        id: occurrenceStart.isSame(baseStart, "day") ? item.id : `${item.id}-${occurrenceKey}`,
        startAt: dayjs(item.startAt).add(offset, "millisecond").toISOString(),
        endAt: dayjs(item.endAt).add(offset, "millisecond").toISOString(),
    };
};

export const expandRepeatingCalendarItems = (items: CalendarItem[], range: DateRange): CalendarItem[] => {
    const rangeStart = range.start.startOf("day");
    const rangeEnd = range.end.endOf("day");
    const maxOccurrences = 1500;

    return items.flatMap((item) => {
        const repeatUnit = getItemRepeatUnit(item as RepeatingCalendarItem);
        if (!repeatUnit) return [item];

        const baseStart = hasScheduledCalendarFields(item)
            ? dayjs(item.startAt).startOf("day")
            : getTaskStartDay(item);
        const baseEnd = hasScheduledCalendarFields(item)
            ? dayjs(item.endAt).startOf("day")
            : getTaskEndDay(item);
        if (!baseStart.isValid()) return [item];
        if (baseStart.isAfter(rangeEnd)) return [];

        const occurrences: CalendarItem[] = [];
        const spanDays = Math.max(0, baseEnd.diff(baseStart, "day"));
        const spanOccurrences = repeatUnit === "day"
            ? spanDays + 1
            : repeatUnit === "week"
                ? Math.ceil(spanDays / 7) + 1
                : 1;
        const skipOccurrences = Math.max(0, rangeStart.diff(baseStart, repeatUnit) - spanOccurrences);
        let occurrenceStart = baseStart.add(skipOccurrences, repeatUnit);
        let generatedCount = 0;

        while (!occurrenceStart.isAfter(rangeEnd) && generatedCount <= maxOccurrences) {
            const occurrence = hasScheduledCalendarFields(item)
                ? shiftCalendarItemOccurrence(item, occurrenceStart, baseStart)
                : shiftCalendarTaskOccurrence(item, occurrenceStart, baseStart);

            if (
                (hasScheduledCalendarFields(occurrence) && !dayjs(occurrence.endAt).isBefore(rangeStart) && !dayjs(occurrence.startAt).isAfter(rangeEnd))
                || (hasActionCalendarFields(occurrence) && !getTaskEndDay(occurrence).isBefore(rangeStart) && !getTaskStartDay(occurrence).isAfter(rangeEnd))
            ) {
                occurrences.push(occurrence);
            }

            const nextOccurrenceStart = occurrenceStart.add(1, repeatUnit);
            if (!nextOccurrenceStart.isAfter(occurrenceStart)) break;
            occurrenceStart = nextOccurrenceStart;
            generatedCount += 1;
        }

        return occurrences;
    });
};

export const isMultiDayTask = (task: CalendarItem): boolean => !getTaskEndDay(task).isSame(getTaskStartDay(task), "day");

export const getTaskDateTimeRange = (task: CalendarItem): DateRange => {
    if (task.allDay) {
        const start = dayjs(task.startDate || task.dueDate).startOf("day");
        const end = dayjs(task.endDate || task.startDate || task.dueDate).endOf("day");
        return { start, end: end.isAfter(start) ? end : start.endOf("day") };
    }
    const start = dayjs(`${task.startDate || task.dueDate}T${task.startTime || task.dueTime || "09:00"}`);
    const end = dayjs(`${task.endDate || task.startDate || task.dueDate}T${task.endTime || task.startTime || task.dueTime || "10:00"}`);
    return { start, end: end.isAfter(start) ? end : start.add(45, "minute") };
};

export const getReminderOffsetMinutes = (value?: ReminderOffset | null): number | null => {
    if (value === undefined || value === null || value === "" || value === "none") return null;
    const minutes = Number(value);
    return Number.isFinite(minutes) ? minutes : null;
};

export const getReminderLabel = (value?: ReminderOffset | null): string => REMINDER_LABELS[value || "none"] || REMINDER_LABELS.none;

export const getReminderAnchorDateTime = (item: CalendarItem | null | undefined): Dayjs | null => {
    if (!item) return null;
    if (hasScheduledCalendarFields(item)) {
        if (item.allDay) return dayjs(item.startAt).startOf("day").hour(9).minute(0).second(0);
        return dayjs(item.startAt);
    }
    if (item.allDay) return dayjs(`${item.startDate || item.dueDate}T09:00`);
    return dayjs(`${item.startDate || item.dueDate}T${item.startTime || item.dueTime || "09:00"}`);
};

export const getReminderPopupKey = (item: CalendarItem): string => {
    const reminderMinutes = getReminderOffsetMinutes(item.reminderOffset);
    const anchor = getReminderAnchorDateTime(item);
    if (reminderMinutes === null || !anchor?.isValid()) return "";
    return `${getCalendarItemMode(item)}-${item.id}-${item.updatedAt || item.createdAt || ""}-${anchor.subtract(reminderMinutes, "minute").toISOString()}`;
};

export const formatReminderItemTime = (item: CalendarItem, timezone: string): string => {
    if (hasScheduledCalendarFields(item)) {
        return item.allDay ? "All day" : formatScheduledItemTime(item, timezone).split(" - ")[0];
    }
    return formatTaskDueTime(item, timezone);
};

export const getSuggestedDuration = (taskType: TaskType | ""): number => {
    if (taskType === TASK_TYPES.MEETING) return 60;
    if (taskType === TASK_TYPES.REMINDER) return 30;
    return 45;
};

export const buildBusyWindows = ({ selectedDay, visibleScheduledItems, visibleTasks, timezone }: {
    selectedDay: Dayjs;
    visibleScheduledItems: CalendarItem[];
    visibleTasks: CalendarItem[];
    timezone?: string;
}): BusyWindow[] => {
    const dayStart = selectedDay.startOf("day").hour(8).minute(0);
    const dayEnd = selectedDay.startOf("day").hour(18).minute(0);
    const busyWindows: BusyWindow[] = [];

    visibleScheduledItems
        .filter(item => !isAutomationItem(item))
        .filter(item => scheduledItemOverlapsDate(item, selectedDay, timezone))
        .forEach((item) => {
            if (item.allDay) {
                busyWindows.push({ start: dayStart, end: dayEnd, label: item.title });
                return;
            }
            const start = timezone ? getDateTimeInTimezone(item.startAt, timezone) : dayjs(item.startAt);
            const end = timezone ? getDateTimeInTimezone(item.endAt, timezone) : dayjs(item.endAt);
            if (!start.isValid() || !end.isValid()) return;
            busyWindows.push({
                start: start.isBefore(dayStart) ? dayStart : start,
                end: end.isAfter(dayEnd) ? dayEnd : end,
                label: item.title,
            });
        });

    visibleTasks
        .filter(task => !isAutomationItem(task))
        .filter(task => !task.completed)
        .filter(task => Boolean(task.startDate || task.dueDate))
        .filter(task => taskOverlapsDate(task, selectedDay))
        .forEach((task) => {
            const { start, end } = getTaskDateTimeRange(task);
            if (!start.isValid() || !end.isValid()) return;
            busyWindows.push({
                start: start.isBefore(dayStart) ? dayStart : start,
                end: end.isAfter(dayEnd) ? dayEnd : end,
                label: task.title,
            });
        });

    const sortedBusyWindows = busyWindows
        .filter(window => window.end.isAfter(window.start))
        .sort((left, right) => left.start.valueOf() - right.start.valueOf());

    return sortedBusyWindows.reduce<BusyWindow[]>((mergedWindows, window) => {
        const previousWindow = mergedWindows[mergedWindows.length - 1];
        if (!previousWindow || window.start.isAfter(previousWindow.end)) {
            mergedWindows.push(window);
            return mergedWindows;
        }
        if (window.end.isAfter(previousWindow.end)) {
            previousWindow.end = window.end;
        }
        return mergedWindows;
    }, []);
};

export const getSmartTimeSuggestion = ({ selectedDay, taskType, visibleScheduledItems, visibleTasks, timezone }: {
    selectedDay: Dayjs;
    taskType: TaskType | "";
    visibleScheduledItems: CalendarItem[];
    visibleTasks: CalendarItem[];
    timezone?: string;
}): SmartTimeSuggestion | null => {
    const durationMinutes = getSuggestedDuration(taskType);
    const busyWindows = buildBusyWindows({ selectedDay, visibleScheduledItems, visibleTasks, timezone });
    const workingDayStart = selectedDay.startOf("day").hour(8).minute(0);
    const now = timezone ? getDateTimeInTimezone(new Date(), timezone) : dayjs();
    const shouldAdvanceToNextHalfHour = now.minute() % 30 !== 0 || now.second() !== 0 || now.millisecond() !== 0;
    const roundedNow = now.second(0).millisecond(0).add(
        shouldAdvanceToNextHalfHour ? 30 - (now.minute() % 30) : 0,
        "minute"
    );
    const dayStart = now.isSame(selectedDay, "day") && roundedNow.isAfter(workingDayStart) ? roundedNow : workingDayStart;
    const dayEnd = selectedDay.startOf("day").hour(18).minute(0);
    const windows = [{ start: dayStart, end: dayStart, label: "Start of day" }, ...busyWindows, { start: dayEnd, end: dayEnd, label: "End of day" }];
    let best: { start: Dayjs; end: Dayjs; score: number; reason: string } | null = null;

    for (let index = 0; index < windows.length - 1; index += 1) {
        const currentEnd = windows[index].end;
        const nextStart = windows[index + 1].start;
        if (!nextStart.isAfter(currentEnd)) continue;
        const gapMinutes = nextStart.diff(currentEnd, "minute");
        if (gapMinutes < durationMinutes) continue;

        const candidateStart = currentEnd.minute() === 0 || currentEnd.minute() === 30
            ? currentEnd
            : currentEnd.add(30 - (currentEnd.minute() % 30), "minute").second(0);
        const candidateEnd = candidateStart.add(durationMinutes, "minute");
        if (candidateEnd.isAfter(nextStart)) continue;

        const midpointScore = Math.abs(candidateStart.diff(selectedDay.startOf("day").hour(11), "minute"));
        const gapScore = gapMinutes - durationMinutes;
        const score = midpointScore + (gapScore / 3);

        if (!best || score < best.score) {
            best = {
                start: candidateStart,
                end: candidateEnd,
                score,
                reason: windows[index].label === "Start of day"
                    ? `Open first block before ${windows[index + 1].label}`
                    : windows[index + 1].label === "End of day"
                        ? `Fits after ${windows[index].label}`
                        : `Best gap between ${windows[index].label} and ${windows[index + 1].label}`,
            };
        }
    }

    if (!best) return null;
    return {
        startDate: best.start.format("YYYY-MM-DD"),
        startTime: best.start.format("HH:mm"),
        endDate: best.end.format("YYYY-MM-DD"),
        endTime: best.end.format("HH:mm"),
        durationMinutes,
        reason: best.reason,
    };
};

export const buildWeekSpanLanes = (days: Dayjs[], items: CalendarItem[], timezone?: string): WeekSpanLane[] => {
    const weekStart = days[0].startOf("day");
    const weekEnd = days[6].startOf("day");
    const segments = items
        .map((item) => {
            const itemStart = hasScheduledCalendarFields(item) ? (timezone ? getDateInTimezone(item.startAt, timezone) : dayjs(item.startAt)).startOf("day") : getTaskStartDay(item);
            const itemEnd = hasScheduledCalendarFields(item) ? (timezone ? getDateInTimezone(item.endAt, timezone) : dayjs(item.endAt)).startOf("day") : getTaskEndDay(item);
            if (itemEnd.isBefore(weekStart) || itemStart.isAfter(weekEnd)) return null;
            const visibleStart = itemStart.isBefore(weekStart) ? weekStart : itemStart;
            const visibleEnd = itemEnd.isAfter(weekEnd) ? weekEnd : itemEnd;
            const colStart = visibleStart.diff(weekStart, "day") + 1;
            const colEnd = visibleEnd.diff(weekStart, "day") + 2;
            return {
                item,
                startsInWeek: itemStart.isSame(visibleStart, "day"),
                endsInWeek: itemEnd.isSame(visibleEnd, "day"),
                colStart,
                colEnd,
            };
        })
        .filter((segment): segment is WeekSpanLane[number] => Boolean(segment))
        .sort((left, right) => {
            if (left.startsInWeek !== right.startsInWeek) {
                return left.startsInWeek ? 1 : -1;
            }
            if (left.colStart !== right.colStart) return left.colStart - right.colStart;
            return (right.colEnd - right.colStart) - (left.colEnd - left.colStart);
        });

    const lanes: WeekSpanLane[] = [];
    segments.forEach((segment) => {
        let laneIndex = lanes.findIndex(lane => lane.every(existing => existing.colEnd <= segment.colStart || existing.colStart >= segment.colEnd));
        if (laneIndex === -1) {
            laneIndex = lanes.length;
            lanes.push([]);
        }
        lanes[laneIndex].push(segment);
    });
    return lanes;
};

export const getMonthWeekItemsOffset = (barLanes?: number): string => {
    if (!barLanes) return `${MONTH_SPAN_TOP_OFFSET}px`;
    return `${MONTH_SPAN_TOP_OFFSET + (barLanes * MONTH_SPAN_ITEM_HEIGHT) + ((barLanes - 1) * MONTH_SPAN_ROW_GAP) + 4}px`;
};

export const getMonthWeekRowHeight = ({ barLanes, maxDayItemsInWeek }: { barLanes: number; maxDayItemsInWeek: number }): number => {
    const itemsOffset = parseInt(getMonthWeekItemsOffset(barLanes), 10);
    const itemRowsHeight = maxDayItemsInWeek > 0
        ? (maxDayItemsInWeek * MONTH_DAY_ITEM_HEIGHT) + ((maxDayItemsInWeek - 1) * MONTH_DAY_ITEM_GAP)
        : 0;
    return Math.max(
        MONTH_ROW_MIN_HEIGHT,
        itemsOffset + itemRowsHeight + MONTH_DAY_FOOTER_SPACE + MONTH_DAY_EXTRA_BOTTOM_SPACE
    );
};

export const formatInTimezone = (value: string | number | Date, timezone: string, options: Intl.DateTimeFormatOptions): string => new Intl.DateTimeFormat("en-AU", {
    timeZone: timezone,
    ...options,
}).format(new Date(value));

export const getTimezoneDisplayName = (timezone: string): string => getTimezonePlaceName(timezone);

export const countryCodeToFlag = (countryCode: string): string => countryCode
    .toUpperCase()
    .replace(/./g, char => String.fromCodePoint(127397 + char.charCodeAt(0)));

export const getTimezoneFlag = (timezone: string): string => {
    const countryCodes = timezoneCountryCodes[timezone];
    if (countryCodes?.length) return countryCodes.map(countryCodeToFlag).join("");
    if (timezone.startsWith("Australia/")) return "🇦🇺";
    if (timezone.startsWith("America/")) return "🌎";
    if (timezone.startsWith("Europe/")) return "🇪🇺";
    if (timezone.startsWith("Asia/")) return "🌏";
    if (timezone.startsWith("Africa/")) return "🌍";
    if (timezone.startsWith("Pacific/")) return "🏝️";
    return "🌐";
};

export const getTimezoneOffsetInfo = (timezone: string): { label: string; minutes: number } => {
    const offset = new Intl.DateTimeFormat("en-AU", {
        timeZone: timezone,
        timeZoneName: "shortOffset",
    }).formatToParts(new Date()).find(part => part.type === "timeZoneName")?.value || "GMT";
    const match = offset.match(/^GMT([+-])(\d{1,2})(?::(\d{2}))?$/);
    if (!match) return { label: offset, minutes: 0 };
    const sign = match[1] === "-" ? -1 : 1;
    return {
        label: offset,
        minutes: sign * ((Number(match[2]) * 60) + Number(match[3] || 0)),
    };
};

export const getTimezoneLocalParts = (value: string | number | Date, timezone: string): { weekday: string; hour: number; minute: string } => {
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: timezone,
        weekday: "short",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    }).formatToParts(new Date(value));
    const get = (type: Intl.DateTimeFormatPartTypes) => parts.find(part => part.type === type)?.value || "";
    return {
        weekday: get("weekday"),
        hour: Number(get("hour")),
        minute: get("minute"),
    };
};

export const buildTimezoneCalculator = ({ selectedDay, durationMinutes, timezone, attendeeTimezones }: {
    selectedDay: Dayjs;
    durationMinutes: number;
    timezone: string;
    attendeeTimezones: string[];
}): TimezoneCalculator => {
    const zones = Array.from(new Set([timezone, ...attendeeTimezones])).filter(Boolean);
    if (zones.length === 0) return { hours: [], recommendations: [], matchZones: [] };
    const matchZones = zones.map((zone, index) => ({
        timezone: zone,
        city: getTimezoneDisplayName(zone),
        label: formatTimezoneLabel(zone),
        role: index === 0 ? "Your timezone" : "Attendee timezone",
    }));

    const hours = Array.from({ length: 24 }, (_, hour) => {
        const start = selectedDay.startOf("day").hour(hour).minute(0).second(0);
        const breakdown = zones.map((zone) => {
            const local = getTimezoneLocalParts(start.toDate(), zone);
            const localHour = local.hour === 24 ? 0 : local.hour;
            const score = localHour >= 9 && localHour < 17 ? 2 : localHour >= 7 && localHour < 21 ? 1 : 0;
            return {
                timezone: zone,
                city: getTimezoneDisplayName(zone),
                localLabel: `${local.weekday} ${String(localHour).padStart(2, "0")}:${local.minute}`,
                score,
            };
        });
        const score = breakdown.reduce((sum, item) => sum + item.score, 0);
        const maxScore = zones.length * 2;
        const tone: TimezoneTone = score >= maxScore ? "prime" : score >= zones.length ? "decent" : "avoid";
        const end = start.add(durationMinutes, "minute");
        return {
            hour,
            start,
            end,
            score,
            maxScore,
            tone,
            breakdown,
            startDate: start.format("YYYY-MM-DD"),
            startTime: start.format("HH:mm"),
            endDate: end.format("YYYY-MM-DD"),
            endTime: end.format("HH:mm"),
        };
    });

    const recommendations = hours
        .filter(hour => hour.tone !== "avoid")
        .sort((left, right) => {
            if (right.score !== left.score) return right.score - left.score;
            return Math.abs(left.hour - 10) - Math.abs(right.hour - 10);
        })
        .slice(0, 3);

    return { hours, recommendations, matchZones };
};

export const getCurrentDateInTimezone = (timezone: string): Dayjs => {
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(new Date());
    const year = parts.find((part) => part.type === "year")?.value;
    const month = parts.find((part) => part.type === "month")?.value;
    const day = parts.find((part) => part.type === "day")?.value;
    return dayjs(`${year}-${month}-${day}`);
};

const getDateTimeValue = (value: string | number | Date): Date => value instanceof Date ? value : new Date(value);

export const getDateInTimezone = (value: string | number | Date, timezone: string): Dayjs => {
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(getDateTimeValue(value));
    const year = parts.find((part) => part.type === "year")?.value;
    const month = parts.find((part) => part.type === "month")?.value;
    const day = parts.find((part) => part.type === "day")?.value;
    return dayjs(`${year}-${month}-${day}`);
};

export const getDateTimeInTimezone = (value: string | number | Date, timezone: string): Dayjs => {
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
    }).formatToParts(getDateTimeValue(value));
    const year = parts.find((part) => part.type === "year")?.value;
    const month = parts.find((part) => part.type === "month")?.value;
    const day = parts.find((part) => part.type === "day")?.value;
    const hour = parts.find((part) => part.type === "hour")?.value;
    const minute = parts.find((part) => part.type === "minute")?.value;
    const second = parts.find((part) => part.type === "second")?.value;
    return dayjs(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
};

export const getHolidayFooterLabel = (labels?: string[]): string => {
    if (!labels?.length) return "";
    if (labels.length === 1) return labels[0];
    return `${labels[0]} +${labels.length - 1}`;
};

export const formatScheduledItemTime = (item: CalendarItem, timezone: string): string => {
    if (item.allDay) return "All day";
    const start = formatInTimezone(item.startAt, timezone, { hour: "numeric", minute: "2-digit", hour12: true }).replace(/\s?(am|pm)$/i, match => ` ${match.trim().toUpperCase()}`);
    const end = formatInTimezone(item.endAt, timezone, { hour: "numeric", minute: "2-digit", hour12: true }).replace(/\s?(am|pm)$/i, match => ` ${match.trim().toUpperCase()}`);
    return `${start} - ${end}`;
};

export const formatTaskDueTime = (task: Pick<CalendarItem, "dueDate" | "dueTime"> & Partial<Pick<CalendarItem, "allDay">>, timezone: string): string => {
    if (task.allDay) return "All day";
    if (!task.dueTime) return "EOD";
    const value = new Date(`${task.dueDate}T${task.dueTime}:00`);
    return formatInTimezone(value, timezone, { hour: "numeric", minute: "2-digit", hour12: true }).replace(/\s?(am|pm)$/i, match => ` ${match.trim().toUpperCase()}`);
};

export const statusLabel = (status?: string): string => {
    if (status === "done") return "Done";
    return "Active";
};

export const priorityLabelText = (priority: TaskPriority): string => {
    if (priority === TASK_PRIORITIES.HIGH) return "High";
    if (priority === TASK_PRIORITIES.LOW) return "Low";
    return "Medium";
};

export const checklistPriorityRank: Record<string, number> = {
    High: 0,
    Medium: 1,
    Low: 2,
    "No Priority": 3,
    None: 3,
};

export const checklistPriorityTone: Record<string, { background: string; color: string }> = {
    High: { background: "rgba(239, 68, 68, 0.16)", color: "#ef4444" },
    Medium: { background: "rgba(249, 115, 22, 0.16)", color: "#f97316" },
    Low: { background: "rgba(34, 197, 94, 0.16)", color: "#22c55e" },
    "No Priority": { background: "rgba(148, 163, 184, 0.18)", color: "#94a3b8" },
    None: { background: "rgba(148, 163, 184, 0.18)", color: "#94a3b8" },
};

export const getGoalProgress = (item: CalendarItem): number => {
    if (hasActionCalendarFields(item) && item.completed) return 100;
    const automationDetail = item.automationDetail || {};
    const goalStartDate = dayjs(automationDetail.startDate || (hasScheduledCalendarFields(item) ? item.startAt : item.startDate || item.dueDate)).startOf("day");
    const goalEndDate = dayjs(automationDetail.finishDate || (hasScheduledCalendarFields(item) ? item.endAt : item.endDate || item.startDate || item.dueDate)).startOf("day");
    const now = dayjs();

    if (!goalStartDate.isValid() || !goalEndDate.isValid()) return 0;
    if (!goalEndDate.isAfter(goalStartDate)) return now.isAfter(goalEndDate) ? 100 : 0;
    if (now.isBefore(goalStartDate)) return 0;
    if (now.isAfter(goalEndDate)) return 100;

    const total = goalEndDate.diff(goalStartDate, "millisecond");
    const elapsed = now.diff(goalStartDate, "millisecond");
    return Math.max(0, Math.min(100, Math.round((elapsed / total) * 100)));
};

export const buildPrefillFromSlot = ({
    slot,
    title,
    taskType,
    audience = SHARED_AUDIENCE.PERSONAL,
    priority = TASK_PRIORITIES.MEDIUM,
    repeat = TASK_REPEAT_OPTIONS.NONE,
    description = "",
}: {
    slot: SmartTimeSuggestion | null;
    title: string;
    taskType: TaskType;
    audience?: SharedAudience;
    priority?: TaskPriority;
    repeat?: TaskRepeatOption;
    description?: string;
}): TaskPrefill | null => {
    if (!slot) return null;
    return {
        title,
        description,
        startDate: slot.startDate,
        startTime: slot.startTime,
        endDate: slot.endDate,
        endTime: slot.endTime,
        completed: false,
        taskType,
        priority,
        color: TASK_TYPE_COLORS[taskType] || AUDIENCE_COLORS[audience],
        repeat,
        audience,
        invites: "",
        location: "",
        url: "",
        attachments: [],
    };
};

export const getDateSeries = (startDate: Dayjs, days: number, offset = 0): Dayjs[] => Array.from({ length: days }, (_, index) => startDate.startOf("day").add(offset + index, "day"));

export const findFirstSuggestedSlot = ({ days, taskType, visibleScheduledItems, visibleTasks }: {
    days: Dayjs[];
    taskType: TaskType;
    visibleScheduledItems: CalendarItem[];
    visibleTasks: CalendarItem[];
}): SmartTimeSuggestion | null => {
    for (const day of days) {
        const slot = getSmartTimeSuggestion({
            selectedDay: day,
            taskType,
            visibleScheduledItems,
            visibleTasks,
        });
        if (slot) return slot;
    }
    return null;
};

export const findPreferredSlot = ({
    day,
    durationMinutes,
    preferredHour,
    preferredMinute = 0,
    taskType,
    visibleScheduledItems,
    visibleTasks,
}: {
    day: Dayjs;
    durationMinutes: number;
    preferredHour: number;
    preferredMinute?: number;
    taskType?: TaskType;
    visibleScheduledItems: CalendarItem[];
    visibleTasks: CalendarItem[];
}): SmartTimeSuggestion | null => {
    const selectedDay = day.startOf("day");
    const start = selectedDay.hour(preferredHour).minute(preferredMinute).second(0);
    const end = start.add(durationMinutes, "minute");
    const busyWindows = buildBusyWindows({ selectedDay, visibleScheduledItems, visibleTasks });
    const overlaps = busyWindows.some((window) => start.isBefore(window.end) && end.isAfter(window.start));

    if (!overlaps && end.isSame(selectedDay, "day") && end.hour() <= 18) {
        return {
            startDate: start.format("YYYY-MM-DD"),
            startTime: start.format("HH:mm"),
            endDate: end.format("YYYY-MM-DD"),
            endTime: end.format("HH:mm"),
        };
    }

    return taskType ? getSmartTimeSuggestion({ selectedDay, taskType, visibleScheduledItems, visibleTasks }) : null;
};

export const getItemStartValue = (item: CalendarItem, timezone?: string): number => {
    if (hasScheduledCalendarFields(item)) return (timezone ? getDateTimeInTimezone(item.startAt, timezone) : dayjs(item.startAt)).valueOf();
    return getTaskDateTimeRange(item).start.valueOf();
};

export const monthMatrix = (cursor: Dayjs): Dayjs[] => {
    const monthStart = cursor.startOf("month");
    const offset = (monthStart.day() + 6) % 7;
    const start = monthStart.subtract(offset, "day");
    return Array.from({ length: 42 }, (_, index) => start.add(index, "day"));
};

export const weekMatrix = (cursor: Dayjs): Dayjs[] => {
    const offset = (cursor.day() + 6) % 7;
    const start = cursor.startOf("day").subtract(offset, "day");
    return Array.from({ length: 7 }, (_, index) => start.add(index, "day"));
};

export const yearMatrix = (cursor: Dayjs): Dayjs[] => Array.from({ length: 12 }, (_, index) => cursor.startOf("year").add(index, "month"));

export const HOUR_LABELS = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    label: dayjs().hour(hour).minute(0).format("h A"),
}));

export const normaliseAttachments = (attachments: CalendarAttachment[] | null | undefined): CalendarAttachment[] => attachments || [];

export const getViewStep = (view: ViewOption): ViewStep => {
    if (view === "Day") return { amount: 1, unit: "day" };
    if (view === "Week") return { amount: 1, unit: "week" };
    if (view === "Year") return { amount: 1, unit: "year" };
    return { amount: 1, unit: "month" };
};

export const getViewHeading = (view: ViewOption, cursor: Dayjs): string => {
    if (view === "Day") return cursor.format("dddd D MMMM YYYY");
    if (view === "Week") {
        const weekDays = weekMatrix(cursor);
        return `${weekDays[0].format("D MMM")} - ${weekDays[6].format("D MMM YYYY")}`;
    }
    if (view === "Year") return cursor.format("YYYY");
    return cursor.format("MMMM YYYY");
};

export const getAnalyticsVisibilityLabel = (value: AnalyticsVisibility): string => {
    if (value === "Employees") return getCustomGroupName("employees");
    if (value === "Clients") return getCustomGroupName("clients");
    if (value === "Both") return `${getCustomGroupName("employees")} & ${getCustomGroupName("clients")}`;
    return value;
};

export const withAlpha = (color: string, alpha: string): string => (/^#[0-9a-f]{6}$/i.test(color) ? `${color}${alpha}` : color);

export const getCalendarPillColors = (color: string) => {
    return {
        background: withAlpha(color, "2B"),
        dot: color,
        text: "var(--calendar-text)",
    };
};

export const getAnalyticsColorGradient = (color: string): string => `linear-gradient(180deg, ${withAlpha(color, "F5")} 0%, ${color} 58%, ${withAlpha(color, "B8")} 100%)`;

export const getAnalyticsSourceGradient = (source: EventSource, fallbackColor = "var(--calendar-accent)"): string => {
    const color = SOURCE_COLORS[source] || fallbackColor;
    return getAnalyticsColorGradient(color);
};

export const getAnalyticsBarHeight = (value: number, max: number, maxHeight = 122): number => (
    value > 0 ? Math.max(26, Math.round((value / Math.max(max, 1)) * maxHeight)) : 12
);

export const getWeekStart = (date: Dayjs): Dayjs => {
    const offset = (date.day() + 6) % 7;
    return date.startOf("day").subtract(offset, "day");
};

export const getWeekEnd = (date: Dayjs): Dayjs => getWeekStart(date).add(6, "day").endOf("day");

export const getAnalyticsPeriodRange = (period: AnalyticsPeriod, date: Dayjs): DateRange => {
    if (period === "Yearly") return { start: date.startOf("year"), end: date.endOf("year") };
    if (period === "Monthly") return { start: date.startOf("month"), end: date.endOf("month") };
    return { start: getWeekStart(date), end: getWeekEnd(date) };
};

export const getAnalyticsTrendBuckets = (period: AnalyticsPeriod, date: Dayjs): DateBucket[] => {
    if (period === "Yearly") {
        return Array.from({ length: 12 }, (_, index) => {
            const month = date.startOf("year").add(index, "month");
            return {
                label: month.format("MMM"),
                title: month.format("MMMM YYYY"),
                start: month.startOf("month"),
                end: month.endOf("month"),
            };
        });
    }

    if (period === "Monthly") {
        const monthStart = date.startOf("month");
        const monthEnd = date.endOf("month");
        const buckets: DateBucket[] = [];
        let cursor = getWeekStart(monthStart);
        let weekIndex = 1;

        while (cursor.isBefore(monthEnd, "day") || cursor.isSame(monthEnd, "day")) {
            const start = cursor.isBefore(monthStart, "day") ? monthStart.startOf("day") : cursor.startOf("day");
            const rawEnd = cursor.add(6, "day");
            const end = rawEnd.isAfter(monthEnd, "day") ? monthEnd.endOf("day") : rawEnd.endOf("day");
            buckets.push({
                label: `Wk ${weekIndex}`,
                title: `${start.format("D MMM")} - ${end.format("D MMM")}`,
                start,
                end,
            });
            cursor = cursor.add(1, "week");
            weekIndex += 1;
        }

        return buckets;
    }

    return weekMatrix(date).map((day) => ({
        label: day.format("ddd"),
        title: day.format("dddd D MMMM"),
        start: day.startOf("day"),
        end: day.endOf("day"),
    }));
};

export const getAnalyticsHeading = (period: AnalyticsPeriod, date: Dayjs): string => {
    if (period === "Yearly") return date.format("YYYY");
    if (period === "Monthly") return date.format("MMMM YYYY");
    const start = getWeekStart(date);
    const end = getWeekEnd(date);
    return `${start.format("D MMM")} - ${end.format("D MMM YYYY")}`;
};

export const getCalendarViewDateRange = (view: ViewOption, cursor: Dayjs, selectedDate: Dayjs): DateRange => {
    if (view === "Day") {
        return { start: selectedDate.startOf("day"), end: selectedDate.endOf("day") };
    }
    if (view === "Week") {
        const days = weekMatrix(cursor);
        return { start: days[0].startOf("day"), end: days[days.length - 1].endOf("day") };
    }
    if (view === "Year") {
        const days = yearMatrix(cursor).flatMap((month) => monthMatrix(month));
        return { start: days[0].startOf("day"), end: days[days.length - 1].endOf("day") };
    }

    const days = monthMatrix(cursor);
    return { start: days[0].startOf("day"), end: days[days.length - 1].endOf("day") };
};

export const getCalendarItemsDateRange = ({
    activeView,
    analyticsOpen,
    analyticsPeriod,
    cursor,
    selectedDate,
}: {
    activeView: ViewOption;
    analyticsOpen: boolean;
    analyticsPeriod: AnalyticsPeriod;
    cursor: Dayjs;
    selectedDate: Dayjs;
}): DateRange => {
    const viewRange = getCalendarViewDateRange(activeView, cursor, selectedDate);
    const analyticsRange = analyticsOpen ? getAnalyticsPeriodRange(analyticsPeriod, selectedDate) : viewRange;
    const start = viewRange.start.isBefore(analyticsRange.start) ? viewRange.start : analyticsRange.start;
    const end = viewRange.end.isAfter(analyticsRange.end) ? viewRange.end : analyticsRange.end;

    return {
        start: start.subtract(14, "day").startOf("day"),
        end: end.add(14, "day").endOf("day"),
    };
};

export const scheduledItemTouchesHour = (item: CalendarItem, date: Dayjs, hour: number, timezone?: string): boolean => {
    if (item.allDay) return false;
    const slotStart = date.startOf("day").hour(hour);
    const slotEnd = slotStart.add(1, "hour");
    const itemStart = timezone ? getDateTimeInTimezone(item.startAt, timezone) : dayjs(item.startAt);
    const itemEnd = timezone ? getDateTimeInTimezone(item.endAt, timezone) : dayjs(item.endAt);
    return itemStart.isBefore(slotEnd) && itemEnd.isAfter(slotStart);
};

export const syncRangeFromStartDate = <T extends { startDate: string; endDate: string }>(prev: T, startDate: string): T => {
    if (!startDate) return { ...prev, startDate };
    const next = { ...prev, startDate };
    if (!prev.endDate || dayjs(prev.endDate).isBefore(dayjs(startDate), "day")) {
        next.endDate = startDate;
    }
    return next;
};

export const syncRangeFromEndDate = <T extends { startDate: string; endDate: string }>(prev: T, endDate: string): T => {
    if (!endDate) return { ...prev, endDate };
    const next = { ...prev, endDate };
    if (!prev.startDate || dayjs(prev.startDate).isAfter(dayjs(endDate), "day")) {
        next.startDate = endDate;
    }
    return next;
};

export const syncTaskRangeFromStartDate = (prev: CalendarItemForm, startDate: string): CalendarItemForm => {
    if (!startDate) return { ...prev, startDate };
    if (prev.allDay) {
        return {
            ...prev,
            startDate,
            endDate: startDate,
        };
    }
    return syncRangeFromStartDate(prev, startDate);
};

export const syncTaskRangeFromEndDate = (prev: CalendarItemForm, endDate: string): CalendarItemForm => {
    if (!endDate) return { ...prev, endDate };
    if (prev.allDay) {
        return {
            ...prev,
            startDate: endDate,
            endDate,
        };
    }
    return syncRangeFromEndDate(prev, endDate);
};
